import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, UserPlus, UserCheck, Heart,
  MessageCircle, Coins, Sparkles, ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreatorProfilePage = () => {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const auth = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setLoading(true);
    setProfile(null);
    setPosts([]);
    (async () => {
      try {
        const [p, ps] = await Promise.all([
          axios.get(`${API}/profiles/handle/${handle}`, { headers: auth }),
          axios.get(`${API}/profiles/handle/${handle}/posts`),
        ]);
        setProfile(p.data.profile);
        setFollowing(!!p.data.profile.is_following);
        setPosts(ps.data.items || []);
      } catch (e) {
        if (e?.response?.status === 404) {
          toast.error("Profile not found");
          setProfile({ notFound: true });
        } else {
          toast.error("Could not load profile.");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [handle]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const toggleFollow = async () => {
    if (!token) {
      navigate("/login");
      return;
    }
    setFollowLoading(true);
    try {
      if (following) {
        await axios.delete(`${API}/profiles/follow/${profile.id}`, { headers: auth });
        setFollowing(false);
        setProfile(p => ({ ...p, stats: { ...p.stats, followers: Math.max(0, p.stats.followers - 1) } }));
      } else {
        await axios.post(`${API}/profiles/follow/${profile.id}`, {}, { headers: auth });
        setFollowing(true);
        setProfile(p => ({ ...p, stats: { ...p.stats, followers: p.stats.followers + 1 } }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update follow");
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!profile || profile.notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="profile-not-found">
        <p className="text-3xl font-black text-ink-950">Profile not found.</p>
        <p className="text-sm text-ink-500 mt-2">This creator may have changed their handle.</p>
        <Link to="/community" className="inline-block mt-6">
          <Button>← Back to Community</Button>
        </Link>
      </div>
    );
  }

  const initial = (profile.display_name || profile.handle || "?")[0].toUpperCase();
  const tierBadge = profile.tier === "ultimate" ? "Official"
    : profile.tier && profile.tier !== "free" ? "Pro" : null;

  // SEO — OG/Twitter cards + page title for crawlers and shares
  const seoTitle = `${profile.display_name || `@${profile.handle}`} · Creator on XEA`;
  const seoDesc = (profile.bio && profile.bio.trim())
    || `Discover AI-generated marketing creatives, designs and CGI videos by @${profile.handle} on XEA.`;
  const seoImage = profile.avatar_url || profile.cover_url || (posts[0] && posts[0].thumbnail_url) || (posts[0] && posts[0].media_url);
  const canonicalUrl = `${window.location.origin}/dashboard/community/u/${profile.id}`;

  return (
    <div data-testid="creator-profile-page" className="max-w-5xl mx-auto px-4 py-6 sm:py-10 space-y-6">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc.slice(0, 160)} />
        <link rel="canonical" href={canonicalUrl} />
        {/* Open Graph */}
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc.slice(0, 200)} />
        <meta property="og:url" content={canonicalUrl} />
        {seoImage && <meta property="og:image" content={seoImage} />}
        <meta property="profile:username" content={profile.handle} />
        {/* Twitter */}
        <meta name="twitter:card" content={seoImage ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc.slice(0, 200)} />
        {seoImage && <meta name="twitter:image" content={seoImage} />}
      </Helmet>

      {/* Cover */}
      <div className="relative rounded-3xl overflow-hidden h-32 sm:h-48 bg-gradient-to-br from-blue-500 via-violet-500 to-coral-500">
        {profile.cover_url && (
          <img src={profile.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        )}
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="profile-back"
          className="absolute top-3 left-3 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 backdrop-blur"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
      </div>

      {/* Identity */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-end px-1 -mt-12 sm:-mt-16">
        <div
          className="w-20 h-20 sm:w-28 sm:h-28 rounded-3xl bg-ink-950 flex items-center justify-center text-white text-3xl sm:text-4xl font-black ring-4 ring-white shrink-0"
          data-testid="profile-avatar"
        >
          {profile.avatar_url
            ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover rounded-3xl" />
            : initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-black text-ink-950 tracking-tight truncate">
              {profile.display_name}
            </h1>
            {tierBadge && (
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold bg-ink-950 text-white px-2 py-0.5 rounded-full">
                {tierBadge}
              </span>
            )}
          </div>
          <p className="text-sm text-ink-500" data-testid="profile-handle">
            @{profile.handle}
          </p>
          {profile.bio && (
            <p className="text-sm text-ink-700 mt-2 max-w-xl" data-testid="profile-bio">
              {profile.bio}
            </p>
          )}
          {(profile.links || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {profile.links.map((l, i) => (
                <a key={i} href={l} target="_blank" rel="noopener noreferrer"
                   className="text-xs text-blue-500 hover:underline inline-flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />
                  {l.replace(/^https?:\/\//, "").replace(/\/$/, "").slice(0, 40)}
                </a>
              ))}
            </div>
          )}
        </div>
        {!profile.is_self && (
          <Button
            type="button"
            onClick={toggleFollow}
            disabled={followLoading}
            data-testid="profile-follow-btn"
            className={following
              ? "bg-white border border-ink-950 text-ink-950 hover:bg-ink-50"
              : "bg-blue-500 hover:bg-blue-600 text-white"}
          >
            {followLoading
              ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
              : following
                ? <UserCheck className="w-4 h-4 mr-1" />
                : <UserPlus className="w-4 h-4 mr-1" />}
            {following ? "Following" : "Follow"}
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2 text-center">
        <Stat label="Posts" value={profile.stats.posts} testId="profile-stat-posts" />
        <Stat label="Followers" value={profile.stats.followers} testId="profile-stat-followers" />
        <Stat label="Following" value={profile.stats.following} testId="profile-stat-following" />
        <Stat label="Tips XEA" value={profile.stats.tips_received} testId="profile-stat-tips" />
      </div>

      {/* Posts */}
      <section>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-3">
          Recent posts
        </p>
        {posts.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-ink-200 p-8 text-center bg-white text-sm text-ink-500" data-testid="profile-no-posts">
            {profile.is_self ? "You haven't published anything yet — share your work to grow your audience." : "No public posts yet."}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {posts.map(p => (
              <Link
                key={p.id}
                to={`/community#post-${p.id}`}
                data-testid={`profile-post-${p.id}`}
                className="relative rounded-2xl overflow-hidden aspect-square bg-ink-100 group"
              >
                {p.kind === "video" ? (
                  <video src={p.media_url} muted playsInline preload="metadata"
                         className="w-full h-full object-cover" />
                ) : (
                  <img src={p.media_url} alt="" loading="lazy"
                       className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] flex gap-2">
                  <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {p.likes_count || 0}</span>
                  <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {p.comments_count || 0}</span>
                  {p.tips_received > 0 && (
                    <span className="flex items-center gap-1 ml-auto"><Coins className="w-3 h-3" /> {p.tips_received}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <div className="text-center pt-6 text-[10px] uppercase tracking-[0.18em] font-bold text-ink-400">
        <Sparkles className="w-3 h-3 inline mr-1" /> made on xe-a.com
      </div>
    </div>
  );
};


const Stat = ({ label, value, testId }) => (
  <div className="bg-white border border-ink-200 rounded-2xl py-3 px-2" data-testid={testId}>
    <p className="text-xl sm:text-2xl font-black text-ink-950 tabular-nums">
      {value || 0}
    </p>
    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">{label}</p>
  </div>
);


export default CreatorProfilePage;
